/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import {
    IPayLoad,
    ITelemetryContent,
    TelemetryConstant,
    getPayloadObject,
    getTelemetryAttributes
} from '@msdyn365-commerce-modules/utilities';
import React from 'react';
import { TenderType } from '@msdyn365-commerce/retail-proxy';
import { DistributorPaymentOptionList } from './distributor-payment-option-list';

/**
 * Distributor Selector Search Form Props interface.
 */
export interface IDistributorSelectorSearchFormProps {
    mainClassName: string;
    shouldDisplayList?: boolean;
    telemetryContent?: ITelemetryContent;
    viewMapText: string;
    viewListText: string;
    paymentFilterMenuHeading: string;
    paymentFilterByHeading: string;
    showLocatorView?: boolean;
    showPaymentTypeFilter?: boolean;
    paymentTypes?: TenderType[];
    filteredPaymentType?: string;
    onPaymentFilterSelected(tenderTypeId: string): void;
    onToggleListMapViewState(): void;
}

/**
 * Simple search form consisting of search text and a search button.
 */
export class DistributorSelectorSearchForm extends React.PureComponent<IDistributorSelectorSearchFormProps> {
    private readonly toggleMapViewAttributes: Msdyn365.IDictionary<string> | undefined;

    public constructor(props: IDistributorSelectorSearchFormProps) {
        super(props);
        const payLoad: IPayLoad = getPayloadObject('click', props.telemetryContent!, TelemetryConstant.ToggleMapView);
        this.toggleMapViewAttributes = getTelemetryAttributes(props.telemetryContent!, payLoad);
    }

    public render(): JSX.Element {
        const {
            mainClassName,
            shouldDisplayList,
            viewMapText,
            viewListText,
            showLocatorView,
            paymentTypes,
            paymentFilterMenuHeading,
            paymentFilterByHeading,
            showPaymentTypeFilter,
            onToggleListMapViewState
        } = this.props;
        const toggleButtonText = shouldDisplayList ? viewMapText : viewListText;
        const toggleButtonClass = shouldDisplayList ? 'view-map' : 'list-view';

        return (
            <div className={`${mainClassName}__search`}>
                <div className={`${mainClassName}__search-header`}>
                    {showLocatorView && (
                        <button
                            className={`${mainClassName}__toggle-view ${toggleButtonClass}`}
                            onClick={onToggleListMapViewState}
                            {...this.toggleMapViewAttributes}
                        >
                            {toggleButtonText}
                        </button>
                    )}
                    {showPaymentTypeFilter &&
                        this.renderPaymentTypeList(this.props, paymentTypes, paymentFilterMenuHeading, paymentFilterByHeading)}
                </div>
            </div>
        );
    }

    /**
     * Method called on payment filter change.
     * @returns - Void.
     */
    private readonly _onChangeHandler = () => async (tenderTypeId: string): Promise<void> => {
        this.props.onPaymentFilterSelected(tenderTypeId);
        return Promise.resolve();
    };

    private readonly renderPaymentTypeList = (
        props: IDistributorSelectorSearchFormProps,
        paymentTypesOptions: TenderType[] | undefined,
        paymentFilterMenuHeading: string | undefined,
        paymentFilterByHeading: string | undefined
    ): JSX.Element | null => {
        return (
            <>
                <div className={`${props.mainClassName}__search-header-payment-heading`}>{paymentFilterByHeading}</div>
                <DistributorPaymentOptionList
                    mainClassName={props.mainClassName}
                    paymentTypesMenuText={paymentFilterMenuHeading}
                    defaultOptionText={props.filteredPaymentType ? undefined : paymentFilterMenuHeading}
                    paymentTypesList={paymentTypesOptions}
                    onChange={this._onChangeHandler()}
                />
            </>
        );
    };
}
